import { useEffect, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { Col, DatePicker, Form, FormProps, Modal, notification, Radio, Row, Select } from 'antd';
import { useGetBookingDetail } from 'hooks/useGetBookingDetail';
import useModal from 'stores/useModal';

import { ReactComponent as IconCancelCalendar } from 'assets/images/cancel-calendar.svg';
import { ReactComponent as IconBedUser } from 'assets/images/bed-user.svg';
import { BookingStatus, FOLIO_BALANCE_CODE } from 'services/api/type/booking.type';
import dayjs from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { postRoomCharge } from 'services/api/module/booking.api';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import { useTranslation } from 'react-i18next';

function PostRoomChargeModal() {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const {
    postRoomCharge: { isOpenPostRoomCharge, bookingLineId },
    closePostRoomCharge
  } = useModal();

  const { data: bookingLineDetail } = useGetBookingDetail(bookingLineId);

  // Helper function to invalidate queries after successful mutation
  const invalidateQueries = useCallback(async () => {
    const queriesToInvalidate = [
      [QUERY_KEYS.GET_GUEST_TRANSACTIONS],
      [QUERY_KEYS.GET_GUEST],
      [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL],
      [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_DETAIL, bookingLineId],
      [QUERY_KEYS.GET_TRANSACTION_HISTORY_LOGS]
    ];

    await Promise.all(
      queriesToInvalidate.map(queryKey => queryClient.invalidateQueries({ queryKey }))
    );
  }, [bookingLineId]);

  // Helper function to determine post option based on booking details
  const getDefaultPostOption = useCallback(() => {
    if (!bookingLineDetail) return 'single';

    if (bookingLineDetail.is_master_room) {
      return 'all_master_room';
    }

    if (bookingLineDetail.group?.booking_lines?.some(item => item.is_master_room)) {
      return 'only_master_room';
    }

    return 'single';
  }, [bookingLineDetail]);

  const { mutateAsync: mutatePostRoomCharge, isPending } = useMutation({
    mutationFn: (params: any = {}) =>
      postRoomCharge({
        id: Number(bookingLineId),
        from_date: params.from_date,
        to_date: params.to_date,
        folio_balance_code: params.folio_balance_code,
        post_option: params.post_option
      })
  });

  useEffect(() => {
    if (bookingLineId) {
      form.resetFields();
    }
  }, [form, bookingLineId]);

  useEffect(() => {
    if (bookingLineId && bookingLineDetail) {
      form.setFieldsValue({
        post_option: getDefaultPostOption()
      });
    }
  }, [bookingLineDetail, bookingLineId, form, getDefaultPostOption]);

  const handleCloseModal = () => {
    closePostRoomCharge();
  };

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    try {
      await mutatePostRoomCharge({
        from_date: values.from_date.format('YYYY-MM-DD'),
        to_date: values.to_date.format('YYYY-MM-DD'),
        folio_balance_code: values.folio_balance_code,
        post_option: values.post_option
      });
      closePostRoomCharge();
      await invalidateQueries();
    } catch (err: any) {
      notification.error({
        message: err.error || t('common.error')
      });
    }
  };

  // Consolidated date calculations
  const dateCalculations = useMemo(() => {
    const latestBookingPrice =
      bookingLineDetail?.booking_prices?.[bookingLineDetail.booking_prices.length - 1];
    const checkIn = bookingLineDetail?.check_in ? dayjs(bookingLineDetail.check_in) : undefined;
    const checkOut = bookingLineDetail?.check_out ? dayjs(bookingLineDetail.check_out) : undefined;

    if (!checkIn || !checkOut) {
      return { minDate: checkIn, maxDate: checkOut };
    }

    let maxDate;
    if (checkIn.isSame(checkOut, 'day')) {
      // Same-day hourly booking
      maxDate = checkIn;
    } else if (latestBookingPrice) {
      const startDate = dayjs(latestBookingPrice.start_date);
      const endDate = dayjs(latestBookingPrice.end_date);

      if (endDate.isSame(startDate, 'day')) {
        // Multi-day booking with same start/end date for latest price
        maxDate = checkOut;
      } else {
        // Multi-day booking with different start/end dates
        maxDate = checkOut.subtract(1, 'day');
      }
    } else {
      maxDate = checkOut.subtract(1, 'day');
    }

    return { minDate: checkIn, maxDate };
  }, [bookingLineDetail]);

  const isMasterRoom = useMemo(() => {
    return bookingLineDetail?.group?.booking_lines?.some(item => item.is_master_room);
  }, [bookingLineDetail]);

  const { minDate, maxDate } = dateCalculations;

  // Generate radio options based on booking details
  const getRadioOptions = useCallback(() => {
    const options = [];
    const masterRoomId = bookingLineDetail?.group?.booking_lines?.find(
      item => item.is_master_room
    )?.id;

    if (isMasterRoom) {
      if (!bookingLineDetail?.is_master_room) {
        options.push({
          value: 'only_master_room',
          label: `${t('cashierPage.postRoomCharge.options.onlyMasterRoom')} #${masterRoomId}`,
          show: true
        });
      }

      if (bookingLineDetail?.is_master_room) {
        options.push({
          value: 'all_master_room',
          label: `${t('cashierPage.postRoomCharge.options.allMasterRoom')} #${masterRoomId}`,
          show: true
        });
      }
    }

    options.push({
      value: 'single',
      label: t('cashierPage.postRoomCharge.options.single'),
      show: true
    });

    if (!isMasterRoom) {
      options.push({
        value: 'party',
        label: `${t('cashierPage.postRoomCharge.options.party')} #${bookingLineDetail?.booking_id}`,
        show: true,
        disabled: !bookingLineDetail?.has_party
      });

      options.push({
        value: 'group',
        label: `${t('cashierPage.postRoomCharge.options.group')} #${bookingLineDetail?.group?.name}`,
        show: true,
        disabled: !bookingLineDetail?.has_group
      });
    }

    return options.filter(option => option.show);
  }, [bookingLineDetail, isMasterRoom, t]);

  return (
    <Modal
      title={
        <div className="modal-change-price-header">
          <h3 className="modal-change-price-header__title">
            {t('cashierPage.postRoomCharge.title')}
          </h3>
          <div className="modal-change-price-header__info">
            <div className="flex items-center" style={{ gap: '4px' }}>
              {bookingLineDetail?.status === BookingStatus.CHECK_IN ? (
                <IconBedUser />
              ) : (
                <IconCancelCalendar />
              )}
              <span style={{ lineHeight: 'normal' }}>
                {bookingLineDetail?.room_name || bookingLineDetail?.room_type_name + ' N/A'}
              </span>
            </div>

            <p className="modal-change-price-header__customer-name">
              {bookingLineDetail?.partner_name || t('bookingPage.booking.guest')}
            </p>
          </div>
        </div>
      }
      centered
      width={500}
      open={isOpenPostRoomCharge}
      onCancel={handleCloseModal}
      okText={t('common.actions.save')}
      okButtonProps={{ className: 'ant-btn-secondary' }}
      onOk={() => form.submit()}
      cancelText={t('common.actions.cancel')}
      closeIcon={null}
      className={classNames('modal-change-price')}
      confirmLoading={isPending}
      destroyOnClose
    >
      <Form
        form={form}
        name="change-price-form"
        layout="vertical"
        style={{ width: '100%' }}
        initialValues={{
          from_date: dayjs(),
          to_date: dayjs(),
          folio_balance_code: 'A'
        }}
        onFinish={onFinish}
        autoComplete="off"
        onValuesChange={(changedValues: any) => {
          if (changedValues.from_date) {
            const fromDate = changedValues.from_date;
            const currentToDate = form.getFieldValue('to_date');

            if (currentToDate && fromDate.isAfter(currentToDate)) {
              form.setFieldsValue({ to_date: fromDate });
            }
          }
        }}
      >
        <div className="change-price-col">
          <div className="change-price-preset">
            <Row>
              <Col span={12} className="flex items-center">
                <b>
                  <span>{t('cashierPage.common.transactionPage')}</span>
                </b>
              </Col>
              <Col span={12}>
                <Form.Item name="folio_balance_code" className="m-0 w-full">
                  <Select options={FOLIO_BALANCE_CODE} />
                </Form.Item>
              </Col>
            </Row>
          </div>
          <div>
            <Row>
              <Col span={12} className="flex items-center">
                <Form.Item
                  label={t('cashierPage.common.fromDate')}
                  name="from_date"
                  rules={[
                    {
                      validator: async (_, value) => {
                        const toDate = form.getFieldValue('to_date');
                        if (toDate && value.isAfter(toDate)) {
                          throw new Error(t('common.validateError.dateCalendar'));
                        }
                      }
                    }
                  ]}
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    className="w-full"
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('cashierPage.common.toDate')}
                  name="to_date"
                  className="m-0 flex-grow"
                >
                  <DatePicker
                    minDate={minDate}
                    maxDate={maxDate}
                    format="DD/MM/YYYY"
                    className="w-full"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item label={t('cashierPage.postRoomCharge.postOptions')} name="post_option">
                  <Radio.Group buttonStyle="outline" className="flex flex-col">
                    {getRadioOptions().map(option => (
                      <Radio key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>
      </Form>
    </Modal>
  );
}

export default PostRoomChargeModal;
